import React from 'react'
import PropTypes from 'prop-types'
import { withStyles } from '@material-ui/styles'
import { ODataLoader } from 'components/dataLoader'
import { LoadingPanel } from './loadingPanel'
import { Grid as KendoGrid, GridToolbar, GridNoRecords, GridColumn } from '@progress/kendo-react-grid'
import { IntlProvider, load, LocalizationProvider } from '@progress/kendo-react-intl'
import { getComponentKey } from 'utils/getComponentKey'
import { localized } from 'decorators'
import { Button } from 'components/buttons'
import { mergeFilters, mergeSorting, filterOutNullFilters } from './filterHelpers'
import { isEmptyObject } from 'utils/isEmptyObject'
import { convertIsoStringsToDates, fromUtcString } from 'utils/dateUtilities'
import { checkboxCell, createCheckBoxHeaderCell } from './checkboxCell'

import { styles } from './grid.css'

import likelySubtags from 'cldr-core/supplemental/likelySubtags.json'
import currencyData from 'cldr-core/supplemental/currencyData.json'
import weekData from 'cldr-core/supplemental/weekData.json'

import csNumbers from 'cldr-numbers-full/main/cs/numbers.json'
import csLocalCurrency from 'cldr-numbers-full/main/cs/currencies.json'
import csCaGregorian from 'cldr-dates-full/main/cs/ca-gregorian.json'
import csDateFields from 'cldr-dates-full/main/cs/dateFields.json'

load(
  likelySubtags,
  currencyData,
  weekData,
  csNumbers,
  csLocalCurrency,
  csCaGregorian,
  csDateFields
)

const DEFAULT_PAGE_SIZE = 20

const convertDateFilters = (filters, dateProperties = []) => {
  if (filters && filters.length > 0 && dateProperties.length > 0) {
    return filters.map(filter => {
      if (dateProperties.find(x => filter.field === x)) {
        return { ...filter, value: fromUtcString(filter.value) }
      }
      return filter
    })
  }
  return filters
}
const processPersistedState = props => {
  const defaultState = {
    sort: props.defaultSort || [],
    filter: props.defaultFilter || {},
    skip: props.defaultSkip || 0,
    pageSize: props.pageSize || DEFAULT_PAGE_SIZE
  }
  const { persistor } = props
  return (persistor ? persistor.get(defaultState) : defaultState)
}
const prepareRequest = (currentRequest, staticFilters, staticSorting) => {
  return {
    ...currentRequest,
    sort: mergeSorting(currentRequest.sort, staticSorting),
    filter: mergeFilters(filterOutNullFilters(currentRequest.filter), staticFilters)
  }
}

const convertDates = (data, propertyNames = []) => {
  if (data && data.length && propertyNames.length) {
    return data.map(item => convertIsoStringsToDates(item, propertyNames))
  }
  return data
}
class Grid extends React.Component {
  constructor (props, context) {
    super(props, context)

    const { sort, filter: rawFilter, pageSize, skip } = processPersistedState(props)
    const filter = rawFilter && rawFilter.filters ? { ...rawFilter, filters: convertDateFilters(rawFilter.filters, props.dateProperties) } : rawFilter
    const gridState = {
      skip,
      take: pageSize || DEFAULT_PAGE_SIZE,
      sort,
      filter: props.filterable ? filter : {}
    }
    const { persistor } = props
    this.state = {
      gridState,
      pageSize: pageSize || DEFAULT_PAGE_SIZE,
      refreshToken: getComponentKey(),
      request: prepareRequest(gridState, props.staticFilters, props.staticSorting),
      selectedIds: [],
      isMultiselectEnabled: persistor ? persistor.getPersisted(`${persistor.key()}_multiselect`, false) : false
    }
  }
  componentDidUpdate (prevProps) {
    if (this.props.staticFilters !== prevProps.staticFilters) {
      this.setState({
        request: prepareRequest(this.state.gridState, this.props.staticFilters, this.props.staticSorting),
        selectedIds: []
      })
    }
  }
  persistAndBuildNewRequest = ({ gridState, pageSize}) => {
    const { persistor } = this.props
    if (persistor) {
      const { sort, filter, skip } = gridState
      persistor.set({ sort, filter, pageSize, skip })
    }
    const request = prepareRequest(gridState, this.props.staticFilters, this.props.staticSorting )
    this.setState({ request, gridState, pageSize })
  }
  setFilter = (filter) => {
    this.persistAndBuildNewRequest({ gridState: { ...this.state.gridState, filter, skip: 0 }, pageSize: this.state.pageSize })
  }
  onSortChange = e => {
    this.persistAndBuildNewRequest({ gridState: { ...this.state.gridState, sort: e.sort }, pageSize: this.state.pageSize })
  }
  onFilterChange = e => {
    this.setFilter(e.filter)
  }
  onPageChange = e => {
    this.persistAndBuildNewRequest({ gridState: { ...this.state.gridState, skip: e.page.skip, take: e.page.take }, pageSize: e.page.take })
  }
  onRefreshClicked = () => {
    this.setState({ refreshToken: getComponentKey() })
  }
  onClearFiltersClicked = () => {
    this.setFilter({})
  }
  handleCheckboxTitleChange = (selectedIds) => {
    this.setState({ selectedIds })
  }
  handleCheckboxRowChange = ({ id, checked }) => {
    const selectedIds = checked ?
      [...this.state.selectedIds, id] :
      this.state.selectedIds.filter(x => x !== id)
    this.setState({ selectedIds })
  }
  handleOnRowClick = (e) => {
    if (typeof this.props.onRowClick === 'function') {
      this.props.onRowClick(e, this.state.selectedIds, this.state.isMultiselectEnabled)
    }
  }
  onExportClicked = () => {
    if (typeof this.props.onExport === 'function') { this.props.onExport() }
  }
  onToggleMultiselect = () => {
    const isEnabled = !this.state.isMultiselectEnabled
    const { persistor } = this.props
    if (persistor) {
      persistor.setPersisted(`${persistor.key()}_multiselect`, isEnabled)
    }
    this.setState({ isMultiselectEnabled: isEnabled, selectedIds: [] })
  }
  onMouseDown = (e) => {
    this.props.preventSelection && e.preventDefault() /* prevents browser text selection */
  }
  setWrapperRef = (el) => {
    this.wrapperDiv = el
  }
  renderKendoGrid = ({ loading, data = [], totalCount, hideRefresh }) => {
    const { children, style, filterable, onRowClick, preventSelection, dateProperties, pageSizes, canExport, isExporting, ...otherProps } = this.props
    const isClickable = !!onRowClick
    const isLoading = loading || this.props.loading
    const wrapperStyle = isClickable ? { cursor: 'pointer' } : {}
    const hasActiveFilter = !isEmptyObject(this.state.gridState.filter)
    const activeFilterCss = hasActiveFilter ? this.props.classes.withActiveFilter : this.props.classes.withoutFilter
    if (this.state.gridState.skip > totalCount) {
      setTimeout(() => this.onPageChange({ page: { skip: 0, take: this.state.pageSize }}), 1)
    }
    const renderToolbar = !hideRefresh || filterable
    const { multiselect } = this.props
    const { selectedIds } = this.state
    return (
      <LocalizationProvider language={this.props.language}>
        <IntlProvider locale={this.props.locale}>
          <div className={`${this.props.classes.root} ${activeFilterCss}`}>
            <div ref={this.setWrapperRef} style={wrapperStyle} onMouseDown={this.onMouseDown}>
              <KendoGrid
                style={(isLoading ? { ...style, minHeight: 300 } : style)}
                className={`${data.length === 0 ? 'no-records-grid ' : ''} ${this.state.isMultiselectEnabled ? 'multiselect-grid' : null }`}
                data={convertDates(data, dateProperties)}
                pageable={{
                  info: true,
                  type: 'input',
                  pageSizes: pageSizes || true,
                  previousNext: true
                }}
                {...this.state.gridState}
                total={totalCount}
                onRowClick={this.handleOnRowClick}
                onPageChange={this.onPageChange}
                sortable
                onSortChange={this.onSortChange}
                filterable={filterable}
                onFilterChange={this.onFilterChange}
                {...otherProps}
              >
                {renderToolbar && <GridToolbar>
                  <div className={this.props.classes.toolbar}>
                    {multiselect && <Button
                      onClick={this.onToggleMultiselect}
                      icon='check-square-o'
                      color='default'
                      title={this.props.localize('common.multiSelectTooltip')}>{this.props.localize('common.multiSelect')}</Button>}
                    {canExport && <Button
                      onClick={this.onExportClicked}
                      icon='file-excel-o'
                      color='default'
                      disabled={false}
                      isLoading={isExporting}
                      title={this.props.localize('common.exportTooltip')}>{this.props.localize('common.export')}</Button>}
                    {!hideRefresh && <Button
                      onClick={this.onRefreshClicked}
                      icon='refresh'
                      color='default'
                      title={this.props.localize('common.refreshTitle')}>{this.props.localize('common.refresh')}</Button>}

                    {filterable && <Button
                      onClick={this.onClearFiltersClicked}
                      disabled={!hasActiveFilter}
                      icon='eraser'
                      color='default'
                      title={this.props.localize('common.clearFilter')}>{this.props.localize('common.clearFilter')}</Button>}

                  </div>
                </GridToolbar>}
                <GridNoRecords>{isLoading ? ' ' : this.props.localize('common.noRecords')}</GridNoRecords>
                {this.state.isMultiselectEnabled && data.length !== 0 && <GridColumn
                  field='id'
                  headerCell={createCheckBoxHeaderCell({
                    selectedIds,
                    data,
                    onChange: this.handleCheckboxTitleChange
                  })}
                  sortable={false}
                  cell={checkboxCell({ selectedIds, onChange: this.handleCheckboxRowChange })}
                  width={60} />}
                {children}
              </KendoGrid>
            </div>
            {isLoading === true && <LoadingPanel gridComponent={this.wrapperDiv} />}
          </div>
        </IntlProvider>
      </LocalizationProvider>
    )
  }
  
  render () {
    const { dataSource } = this.props
    if (typeof dataSource === 'function') {
      return (
        <ODataLoader
          key={`${this.props.refreshToken || ''}_${this.state.refreshToken}`}
          dataSource={dataSource}
          request={this.state.request}
          render={this.renderKendoGrid} />
      )
    } else {
      const { data: d, loading } = this.props
      const data = (loading ? [] : d || [])
      return this.renderKendoGrid({ data, loading, totalCount: data.length, hideRefresh: true })
    }
  }
}

Grid.propTypes = {
  dataSource: PropTypes.func,
  data: PropTypes.array,
  loading: PropTypes.bool,
  filterable: PropTypes.bool,
  onRowClick: PropTypes.func,
  preventSelection: PropTypes.bool,
  multiselect: PropTypes.bool,
  refreshToken: PropTypes.oneOfType([PropTypes.object, PropTypes.number, PropTypes.string])
}
Grid.defaultProps = {
  filterable: true,
  loading: false,
  preventSelection: false
}

export default withStyles(styles)(localized(Grid))
